import { css } from 'aphrodite';
import * as React from 'react';
import type { ISupportTicket } from '../../../models/Support/ISupportTicket';
import {
	useAssignTicketMutation,
	useUpdateTicketCategoryMutation,
	useUpdateTicketPriorityMutation,
	useUpdateTicketStatusMutation,
} from '../../../queries/Support/useSupportTicketMutations';
import { usePrefetchSupportTicket } from '../../../queries/Support/useSupportTicketQuery';
import { getApiErrorMessage } from '../../../utils/Support/supportHelpers';
import { DataGridRow, DataGridRowItem } from '../../../web/components/DataGrid';
import { Button } from '../../../web/components-v2/Button';
import { Checkbox } from '../../../web/components-v2/Checkbox';
import { Pill } from '../../../web/components-v2/Pill';
import { ticketTableStyles as ts } from './styles/ticketTableStyles';
import { formatRelativeTime, getStatusInfo, getTicketContactLabel, PRIORITY_COLOR } from './supportTicketPresentation';
import type { TicketTableColumnKey } from './ticketTableColumns';
import { TicketHoverPopover } from './TicketHoverPopover';
import { type EditMenuOption, PRIORITY_EDIT_OPTIONS, STATUS_EDIT_OPTIONS, TicketEditMenu } from './TicketTableActions';

const EMPTY_CELL = '—';

const TruncatedText: React.FC<{ value?: string; muted?: boolean }> = ({ value, muted }) => {
	const text = value || EMPTY_CELL;
	return (
		<span className={css(ts.truncatedText, muted && ts.cellMuted)} title={text === EMPTY_CELL ? undefined : text}>
			{text}
		</span>
	);
};

interface TicketTableRowProps {
	ticket: ISupportTicket;
	columns: readonly TicketTableColumnKey[];
	category?: { name: string; color?: string };
	assigneeName?: string;
	selected: boolean;
	onToggleSelect: () => void;
	assigneeOptions: ReadonlyArray<EditMenuOption<string>>;
	categoryOptions: ReadonlyArray<EditMenuOption<string>>;
	agentNames: Record<string, string>;
	agentProfilePicMap: Record<string, string>;
	agentProfilePicEmailMap: Record<string, string>;
	onActionError: (message: string | null) => void;
	onOpen: () => void;
}

export const TicketTableRow: React.FC<TicketTableRowProps> = ({
	ticket,
	columns,
	category,
	assigneeName,
	selected,
	onToggleSelect,
	assigneeOptions,
	categoryOptions,
	agentNames,
	agentProfilePicMap,
	agentProfilePicEmailMap,
	onActionError,
	onOpen,
}) => {
	const status = getStatusInfo(ticket.status);
	const prefetchTicket = usePrefetchSupportTicket();
	const updateStatus = useUpdateTicketStatusMutation(ticket.id);
	const assignTicket = useAssignTicketMutation(ticket.id);
	const updatePriority = useUpdateTicketPriorityMutation(ticket.id);
	const updateCategory = useUpdateTicketCategoryMutation(ticket.id);

	const feedback = (failMessage: string) => ({
		onSuccess: () => onActionError(null),
		onError: (error: unknown) => onActionError(getApiErrorMessage(error, failMessage)),
	});
	const stopClick = (event: React.MouseEvent) => event.stopPropagation();
	const contactLabel = getTicketContactLabel(ticket);
	const subject = ticket.subject || EMPTY_CELL;

	const renderCell = (column: TicketTableColumnKey) => {
		switch (column) {
			case 'select':
				return (
					<DataGridRowItem key={column} styles={[ts.cellCheckbox]}>
						<div className={css(ts.cellStopClick)} onClick={stopClick}>
							<Checkbox
								checked={selected}
								onCheckedChange={onToggleSelect}
								aria-label={`Select ticket #${ticket.number}`}
							/>
						</div>
					</DataGridRowItem>
				);
			case 'number':
				return (
					<DataGridRowItem key={column} styles={[ts.cellNumber]}>
						#{ticket.number}
					</DataGridRowItem>
				);
			case 'subject':
				return (
					<DataGridRowItem key={column} styles={[ts.cellTruncate]}>
						<TicketHoverPopover
							ticket={ticket}
							agentNames={agentNames}
							agentProfilePicMap={agentProfilePicMap}
							agentProfilePicEmailMap={agentProfilePicEmailMap}
						>
							<Button
								type='button'
								variant='ghost'
								styles={[ts.subjectBtn]}
								onClick={event => {
									event.stopPropagation();
									onOpen();
								}}
								onFocus={() => prefetchTicket(ticket.id)}
								title={subject}
							>
								{subject}
							</Button>
						</TicketHoverPopover>
					</DataGridRowItem>
				);
			case 'contact':
				return (
					<DataGridRowItem key={column} styles={[ts.cellTruncate]}>
						<TruncatedText value={contactLabel} muted={contactLabel === EMPTY_CELL} />
					</DataGridRowItem>
				);
			case 'account':
				return (
					<DataGridRowItem key={column} styles={[ts.cellTruncate]}>
						<TruncatedText value={ticket.accountName} muted={!ticket.accountName} />
					</DataGridRowItem>
				);
			case 'status':
				return (
					<DataGridRowItem key={column} styles={[ts.cellTruncate]}>
						<div className={css(ts.cellStopClick)} onClick={stopClick}>
							<TicketEditMenu
								options={STATUS_EDIT_OPTIONS}
								onSelect={nextStatus => updateStatus.mutate(nextStatus, feedback('Failed to update ticket status.'))}
								triggerStyles={[ts.cellEditBtn]}
								title='Change status'
							>
								<Pill variant='tag' color={status.color} size='small' label={status.label} />
							</TicketEditMenu>
						</div>
					</DataGridRowItem>
				);
			case 'priority':
				return (
					<DataGridRowItem key={column} styles={[ts.cellTruncate]}>
						<div className={css(ts.cellStopClick)} onClick={stopClick}>
							<TicketEditMenu
								options={PRIORITY_EDIT_OPTIONS}
								onSelect={priority => updatePriority.mutate(priority, feedback('Failed to update priority.'))}
								triggerStyles={[ts.cellEditBtn]}
								title='Change priority'
							>
								{ticket.priority ? (
									<Pill
										variant='tag'
										color={PRIORITY_COLOR[ticket.priority] ?? 'blue'}
										size='small'
										label={ticket.priority}
									/>
								) : (
									EMPTY_CELL
								)}
							</TicketEditMenu>
						</div>
					</DataGridRowItem>
				);
			case 'category':
				return (
					<DataGridRowItem key={column} styles={[ts.cellTruncate]}>
						<div className={css(ts.cellStopClick)} onClick={stopClick}>
							<TicketEditMenu
								options={categoryOptions}
								onSelect={categoryId =>
									updateCategory.mutate(categoryId || null, feedback('Failed to update category.'))
								}
								triggerStyles={[ts.cellEditBtn]}
								title='Change category'
							>
								{category ? <Pill variant='category' color='gray' size='small' label={category.name} /> : EMPTY_CELL}
							</TicketEditMenu>
						</div>
					</DataGridRowItem>
				);
			case 'assignee':
				return (
					<DataGridRowItem key={column} styles={[ts.cellTruncate, ...(assigneeName ? [] : [ts.cellMuted])]}>
						<div className={css(ts.cellStopClick)} onClick={stopClick}>
							<TicketEditMenu
								options={assigneeOptions}
								onSelect={assigneeId => assignTicket.mutate(assigneeId, feedback('Failed to assign ticket.'))}
								triggerStyles={[ts.cellEditBtn]}
								title='Change assignee'
							>
								<TruncatedText value={assigneeName ?? 'Unassigned'} />
							</TicketEditMenu>
						</div>
					</DataGridRowItem>
				);
			case 'lastUpdate':
				return (
					<DataGridRowItem key={column} styles={[ts.cellTime]}>
						{formatRelativeTime(ticket.lastMessageAt)}
					</DataGridRowItem>
				);
			default:
				throw new Error(`Unsupported ticket table column: ${column}`);
		}
	};

	return (
		<DataGridRow styles={[ts.row]} onClick={onOpen} onMouseEnter={() => prefetchTicket(ticket.id)}>
			{columns.map(renderCell)}
		</DataGridRow>
	);
};
